import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TableSettings } from '../types';
import { PARAM_KEYS, paramKey, parseFilters, parseOffset, parseSearch, parseSorting } from './parsers';
import type { DriverState } from './types';

/**
 * Драйвер персистенции состояния таблицы через URL search params.
 *
 * Каждый параметр хранится под ключом `{name}_{param}` (например `dialogs_filters`).
 * Пустые значения удаляются из URL для поддержания чистоты адресной строки.
 */
export const useUrlDriver = (name: string): DriverState => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setParams = useCallback(
    (updates: Partial<Record<keyof typeof PARAM_KEYS, string | null>>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined) continue;
          const k = paramKey(name, key as keyof typeof PARAM_KEYS);
          if (value === null || value === '') next.delete(k);
          else next.set(k, value);
        }
        return next;
      });
    },
    [name, setSearchParams],
  );

  const write = useCallback(
    (patch: Partial<TableSettings>) => {
      const updates: Partial<Record<keyof typeof PARAM_KEYS, string | null>> = {};
      if ('filters' in patch) {
        updates.filters = patch.filters?.length ? JSON.stringify(patch.filters) : null;
      }
      if ('sorting' in patch) {
        updates.sorting = patch.sorting?.length ? JSON.stringify(patch.sorting) : null;
      }
      if ('offset' in patch) {
        updates.offset = patch.offset != null && patch.offset > 0 ? String(patch.offset) : null;
      }
      if ('search' in patch) {
        updates.search = patch.search?.query ?? null;
        updates.searchMode = patch.search?.mode ?? null;
      }
      setParams(updates);
    },
    [setParams],
  );

  return {
    filters: parseFilters(searchParams.get(paramKey(name, 'filters'))),
    sorting: parseSorting(searchParams.get(paramKey(name, 'sorting'))),
    offset: parseOffset(searchParams.get(paramKey(name, 'offset'))),
    search: parseSearch(searchParams.get(paramKey(name, 'search')), searchParams.get(paramKey(name, 'searchMode'))),
    contentSearch: undefined,
    columnOrder: undefined,
    columnVisibility: undefined,
    columnPinning: undefined,
    columnSizing: undefined,
    columnsPanelWidth: undefined,
    columnsPanelExpandedGroups: undefined,
    activeSidePanel: undefined,
    sidePanelPinned: undefined,
    headerOrientation: undefined,
    headerHeight: undefined,
    scrollIndex: undefined,
    scrollKey: undefined,
    dismissedSelectionKey: undefined,
    selectionDescription: undefined,
    write,
    path: `url.${name}`,
  };
};
